import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// Styling applied while the field holds an uncommitted edit — mirrors the
// portal's autosaveInputStyleDirty cue. Shared with AutosaveRichTextInput so
// every autosaving field signals a pending edit the same way.
export const dirtyStyle = 'tw:border-ed-gold! tw:ring-2 tw:ring-ed-gold/30';

interface AutosaveTextInputProps {
  currentValue: string;
  /** Called when the user commits a changed value (blur, or Enter on single-line). */
  onCommitChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  numeric?: boolean;
  /** Hard cap on what can be typed, for a field with a fixed width (a code). */
  maxLength?: number;
  className?: string;
  /**
   * Tidies the draft into the form it is stored in (trimmed, upper-cased) on
   * commit. Applied before both `validate` and the equality check, so a draft
   * that normalises onto the saved value counts as no change — and the field
   * snaps back to the stored spelling rather than sitting there looking unsaved.
   */
  normalise?: (value: string) => string;
  /**
   * Return null when valid, or an error message string. Receives the normalised
   * value. May be async — a check that has to ask the server (a code already in
   * use) is awaited before the value is committed, so a rejected value never
   * reaches autosave.
   */
  validate?: (value: string) => string | null | Promise<string | null>;
}

/**
 * Text/number/textarea input that autosaves on blur (and Enter, for single-line
 * fields). Adapted from the portal's AutosaveTextInput: keeps a local draft
 * value, resets on Escape, and highlights while dirty. Built on the existing
 * shadcn Input/Textarea rather than react-textarea-autosize.
 */
export function AutosaveTextInput({
  currentValue,
  onCommitChange,
  disabled,
  placeholder,
  multiline,
  rows,
  numeric,
  maxLength,
  className,
  normalise,
  validate,
}: AutosaveTextInputProps) {
  const [localValue, setLocalValue] = useState(currentValue);
  const [error, setError] = useState<string | null>(null);
  const escapePressedRef = useRef(false);
  // The draft as it stands right now, readable after an await (state captured in
  // a closure would be the value at the time the blur handler ran). Synced in an
  // effect rather than during render, which React reserves for rendering.
  const localValueRef = useRef(localValue);
  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  // Reset the draft when the underlying value changes (load, undo/redo). Done
  // during render rather than in an effect to avoid a cascading re-render.
  const [lastSynced, setLastSynced] = useState(currentValue);
  if (currentValue !== lastSynced) {
    setLastSynced(currentValue);
    setLocalValue(currentValue);
  }

  const isDirty = localValue !== currentValue;

  async function commit() {
    if (escapePressedRef.current) {
      // Blur triggered by Escape — discard the draft.
      setLocalValue(currentValue);
      setError(null);
      escapePressedRef.current = false;
      return;
    }
    const draft = localValue;
    const committed = normalise ? normalise(draft) : draft;
    if (committed === currentValue) {
      // Nothing to save — but the draft may still be spelled differently from
      // the stored value, so show what is actually stored.
      setLocalValue(currentValue);
      setError(null);
      return;
    }
    if (validate) {
      const validationError = await validate(committed);
      // An async validator answers about the value as it was when the field was
      // left. If the user has typed on since, that answer is about a draft that
      // no longer exists — drop it rather than flag or save the wrong string.
      if (draft !== localValueRef.current) return;
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setError(null);
    setLocalValue(committed);
    onCommitChange(committed);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.nativeEvent.isComposing) return; // let IME (e.g. Japanese) finish
    if (e.key === 'Escape') {
      escapePressedRef.current = true;
      e.currentTarget.blur();
      return;
    }
    // Single-line: Enter commits. Multiline: Enter inserts a newline as usual.
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }

  const sharedProps = {
    value: localValue,
    disabled,
    placeholder,
    maxLength,
    onKeyDown: handleKeyDown,
    onBlur: () => void commit(),
    className: cn(className, isDirty && dirtyStyle),
  };

  return (
    <div>
      {multiline ? (
        <Textarea
          {...sharedProps}
          rows={rows ?? 3}
          onChange={(e) => {
            setLocalValue(e.target.value);
            setError(null);
          }}
        />
      ) : (
        <Input
          {...sharedProps}
          // Numeric fields use type=text + inputMode rather than type=number:
          // type=number's controlled-value quirks swallow the blur-commit, and
          // the parent already parses the string via Number(...).
          type='text'
          inputMode={numeric ? 'numeric' : undefined}
          onChange={(e) => {
            setLocalValue(numeric ? e.target.value.replace(/[^\d.-]/g, '') : e.target.value);
            setError(null);
          }}
        />
      )}
      {error && <p className='tw:mt-1 tw:text-sm tw:text-destructive'>{error}</p>}
    </div>
  );
}
